import React, { useEffect, useRef, useState } from "react";
import AppTooltip from "./AppTooltip";
import { IconChevronRight } from "../icons";

function BreadcrumbOverflowItem({ label, className, href, onClick }) {
  const labelRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const node = labelRef.current;

    if (!node) {
      return undefined;
    }

    function measureOverflow() {
      setIsOverflowing(node.scrollWidth > node.clientWidth);
    }

    measureOverflow();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measureOverflow);
      return () => window.removeEventListener("resize", measureOverflow);
    }

    const resizeObserver = new ResizeObserver(measureOverflow);
    resizeObserver.observe(node);

    return () => {
      resizeObserver.disconnect();
    };
  }, [label]);

  const content = (
    <AppTooltip content={isOverflowing ? label : null} wrapperClassName="ds-breadcrumbs__tooltip-trigger">
      <span ref={labelRef} className={className}>
        {label}
      </span>
    </AppTooltip>
  );

  if (href) {
    return (
      <a className="ds-breadcrumbs__link" href={href} onClick={onClick}>
        {content}
      </a>
    );
  }

  if (onClick) {
    return (
      <button type="button" className="ds-breadcrumbs__link ds-breadcrumbs__link--button" onClick={onClick}>
        {content}
      </button>
    );
  }

  return content;
}

export default function AppBreadcrumbs({ items = [], className = "" }) {
  const normalizedItems = Array.isArray(items)
    ? items.filter((item) => item && typeof item.label === "string" && item.label.trim().length > 0)
    : [];

  if (normalizedItems.length <= 1) {
    return null;
  }

  const lastIndex = normalizedItems.length - 1;
  const classes = ["ds-breadcrumbs", className].filter(Boolean).join(" ");

  function renderItemContent(item, isCurrent) {
    const label = item.label.trim();

    if (isCurrent) {
      return (
        <span className="ds-breadcrumbs__current" aria-current="page">
          {label}
        </span>
      );
    }

    if (item.href) {
      return (
        <BreadcrumbOverflowItem
          label={label}
          className="ds-breadcrumbs__link-label"
          href={item.href}
          onClick={item.onClick}
        />
      );
    }

    if (item.onClick) {
      return (
        <BreadcrumbOverflowItem
          label={label}
          className="ds-breadcrumbs__link-label"
          onClick={item.onClick}
        />
      );
    }

    return <BreadcrumbOverflowItem label={label} className="ds-breadcrumbs__text" />;
  }

  return (
    <nav className={classes} aria-label="Breadcrumb">
      <ol className="ds-breadcrumbs__list">
        {normalizedItems.map((item, index) => {
          const isCurrent = index === lastIndex;

          return (
            <li
              key={`${item.label}-${index}`}
              className={`ds-breadcrumbs__item${isCurrent ? " ds-breadcrumbs__item--current" : ""}`}
            >
              {renderItemContent(item, isCurrent)}
              {!isCurrent ? (
                <span className="ds-breadcrumbs__separator" aria-hidden="true">
                  <IconChevronRight className="ds-breadcrumbs__separator-icon" fontSize="inherit" />
                </span>
              ) : null}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
